<template>
  <div class="ypo-resources page-container-with-header">
    <div class="ypo-resources__header">
      <div class="ypo-resources__header--title">{{ ypoResourcesConstant.TITLE }}</div>
      <div class="ypo-resources__header--toggle">
        <ypo-toggle-button :click-callback="toggleDisplay"
                           :default-toggle-label="ypoResourcesConstant.DEFAULT_TOGGLE_LABEL"
                           :is-toggled-on="!displayAlphabetically"
                           :post-toggle-label="ypoResourcesConstant.POST_TOGGLE_LABEL"
                           post-toggle-color="blue"
                           toggle-default-color="green">
        </ypo-toggle-button>
      </div>
    </div>
    <div class="ypo-resources__content">
      <status-overlay :action="loadingResources" content-height="200px"></status-overlay>
      <div v-if="ypoResources" class="ypo-resources__main">
        <div class="ypo-resources__ad-panel">
          <div v-if="ypoResources.adImage" :title="ypoResources.adImage.alternativeText || ''">
            <v-img :alt="ypoResources.adImage.alternativeText || ''"
                   :src="ypoResources.adImage.imageUrl" class="ypo-resources__ad-panel--image"
                   @click="handleNavigation(ypoResources.adImage.navigationUrl, ypoResources.adImage.isSSOEnabled, ypoResources.adImage.alternativeText || '', 1)"
            />
          </div>
          <div v-if="ypoResources.buttonList" class="ypo-resources__ad-panel--button-list">
            <div v-for="(buttonList, index) in ypoResources.buttonList" :key="index"
                 class="ypo-resources__ad-panel--button">
              <v-tooltip v-if="buttonList.alternativeText" slot="append" top>
                <div
                    slot="activator"
                    :class="['ad-panel-buttons', `button${index}`]" @click="handleNavigation(buttonList.navigationUrl, buttonList.isSSOEnabled, buttonList.label, index+2)"
                >{{ buttonList.label }}
                </div>
                <span>{{ buttonList.alternativeText }}</span>
              </v-tooltip>
              <div v-else
                   :class="['ad-panel-buttons', `button${index}`]"
                   @click="handleNavigation(buttonList.navigationUrl, buttonList.isSSOEnabled, buttonList.label, index+2)"
              >{{ buttonList.label }}
              </div>
              <i v-if="ypoResources.canEditResourceLinks" :data-qe-id="`open_${buttonList.id}_dialog`"
                 class="fas fa-pencil-alt"
                 @click="openEditResourceDialog(buttonList, ypoResourcesCategoryType.BUTTON)"
              ></i>
            </div>
            <div >
              <v-btn flat class="ypo-resources__create-category"
                     v-if="ypoResources.canEditResourceLinks"
                     @click="createNewCategory">
                {{ypoResourcesMenuDialogFields.CREATE_NEW_CATEGORY}}
              </v-btn>
            </div>
          </div>
        </div>
        <div v-if="ypoResources" class="ypo-resources__category-panel">
          <div class="ypo-resources__category-panel--main">
            <div v-if="hasAdminToolsAccess" class="ypo-resources__category">
              <administrator-tools :show-hide-tools="showHideTools"></administrator-tools>
            </div>
            <div v-for="category in categoriesList" v-if="category.isActive" :key="category.id"
                 class="ypo-resources__category desktop-resources">
              <v-card>
                <v-layout row wrap>
                  <v-flex class="ypo-resources__category--header" xs12>{{ category.categoryName }}
                    <i v-if="ypoResources.canEditResourceLinks && !displayAlphabetically" :data-qe-id="`open_${category.id}_dialog`"
                       class="fas fa-pencil-alt"
                       @click="openEditResourceDialog(category, ypoResourcesCategoryType.CATEGORY)"
                    ></i>
                  </v-flex>
                  <v-flex class="ypo-resources__category--content" xs12>
                    <div
                        v-if="ypoResources.canEditResourceLinks && !displayAlphabetically"
                        class="ypo-resources__category--add-btn"
                        data-qe-id="resource-menu-edit_add_btn"
                        @click="openAddResourceDialog(ypoResourcesCategoryType.LINK, category.id)">
                      <i class="fas fa-plus-circle mr-2"></i>{{ ypoResourcesConstant.ADD_NEW_LINK }}
                    </div>
                    <div v-for="(subCategory, index ) in category.subCategories" v-if="showResourceLink(index)"
                         :key="subCategory.id"
                         class="resourceLink">
                      <v-tooltip v-if="subCategory.alternativeText" slot="append" top>
                        <div slot="activator" class="resourceLink__label"
                             @click="handleNavigation(subCategory.navigationUrl, subCategory.isSSOEnabled, subCategory.resourceName )">
                          {{ subCategory.resourceName }}
                        </div>
                        <span>{{ subCategory.alternativeText }}</span>
                      </v-tooltip>
                      <div v-else class="resourceLink__label"
                           @click="handleNavigation(subCategory.navigationUrl, subCategory.isSSOEnabled, subCategory.resourceName )">
                        {{ subCategory.resourceName }}
                      </div>
                      <i v-if="ypoResources.canEditResourceLinks && !displayAlphabetically" :data-qe-id="`open_${subCategory.id}_dialog`"
                         class="fas fa-pencil-alt"
                         @click="openEditResourceDialog(subCategory, ypoResourcesCategoryType.LINK)"
                      ></i>
                    </div>
                    <div v-if="displayShowMoreButton(category)"
                         class="resourceLink__show-more"
                         @click="showMoreLinksForCategory">
                      <div class="resourceLink__show-more--icon">
                        <v-icon class="fas fa-plus"></v-icon>
                      </div>
                    </div>
                  </v-flex>
                </v-layout>
              </v-card>
            </div>
            <div v-for="category in categoriesList" v-if="category.subCategories && category.subCategories.length"
                 :key="`mobile-${category.id}`"
                 class="ypo-resources__category mobile-resources">
              <v-card>
                <v-layout row wrap>
                  <v-flex class="ypo-resources__category--header" xs12>{{ category.categoryName }}</v-flex>
                  <v-flex class="ypo-resources__category--content" xs12>
                    <div v-for="subCategory in category.subCategories" :key="`mobile-${subCategory.id}`"
                         class="resourceLink">
                      <div class="resourceLink__label"
                           @click="handleNavigation(subCategory.navigationUrl, subCategory.isSSOEnabled, subCategory.resourceName )">
                        {{ subCategory.resourceName }}
                      </div>
                    </div>
                  </v-flex>
                </v-layout>
              </v-card>
            </div>
          </div>
        </div>
      </div>
    </div>

    <v-dialog
        v-model="resourceMenuEditDialog"
        content-class="small"
        persistent
        scrollable>
      <v-card class="categoryEdit-popup">
        <div v-if="isSaveInProgress" class="disabled-overlay"></div>
        <v-card-title primary-title>
          {{ headerLabel }}
          <i class="fas fa-times" data-qe-id="close_dialog_btn" @click="closeResourceMenuDialog"></i>
        </v-card-title>
        <v-card-text class="inner-scrollbar">
          <v-form
              ref="resourceMenuDialog"
              class="relative-position"
              v-model="valid"
              lazy-validation>
            <div v-if="deleteTriggered" class="disabled-overlay"></div>
            <v-layout v-if="resourceMenuEditModel && resourceMenuEditModel.type === ypoResourcesCategoryType.CATEGORY" row
                      wrap>
              <v-flex class="details-view">
                <span class="select-label">{{ ypoResourcesMenuDialogFields.DIALOG_TITLE }}</span>
                <v-text-field
                    v-model="resourceMenuEditModel.categoryName"
                    :maxlength="ypoResourcesValidationRules.CATEGORY_NAME_MAX_TEXT_LENGTH"
                    :placeholder="resourceMenuEditModel.categoryName"
                    :rules="ypoResourcesValidationRules.CATEGORY_TITLE_RULE"
                    autofocus
                    color="ypo_blue"
                    required
                >
                </v-text-field>
              </v-flex>
            </v-layout>
            <v-layout v-if="resourceMenuEditModel && resourceMenuEditModel.type === ypoResourcesCategoryType.LINK" row
                      wrap>
              <v-flex class="details-view">
                <span class="select-label">{{ ypoResourcesMenuDialogFields.DIALOG_CATEGORY_LABEL }}</span>
                <v-text-field
                    v-model="resourceMenuEditModel.resourceName"
                    :maxlength="ypoResourcesValidationRules.CATEGORY_NAME_MAX_TEXT_LENGTH"
                    :placeholder="resourceMenuEditModel.resourceName"
                    :rules="ypoResourcesValidationRules.CATEGORY_LABEL_RULE"
                    autofocus
                    color="ypo_blue"
                    required
                >
                </v-text-field>
                <span class="select-label">{{ ypoResourcesMenuDialogFields.DIALOG_ALT_TEXT }}</span>
                <v-text-field
                    v-model="resourceMenuEditModel.alternativeText"
                    :maxlength="ypoResourcesValidationRules.CATEGORY_NAME_MAX_TEXT_LENGTH"
                    :placeholder="resourceMenuEditModel.alternativeText"
                    color="ypo_blue"
                >
                </v-text-field>
                <span class="select-label">{{ ypoResourcesMenuDialogFields.DIALOG_LINK }}</span>
                <v-text-field
                    v-model="resourceMenuEditModel.navigationUrl"
                    :placeholder="resourceMenuEditModel.navigationUrl"
                    :rules="ypoResourcesValidationRules.NAVIGATION_URL_RULE"
                    color="ypo_blue"
                    required
                >
                </v-text-field>
                <v-flex xs2>
                  <span class="select-label">{{ ypoResourcesMenuDialogFields.DIALOG_SSO }}</span>
                  <v-radio-group v-model="resourceMenuEditModel.isSSOEnabled" text-xs-center>
                    <v-flex class="form-control">
                      <v-radio
                          :label="ypoResourcesMenuDialogFields.YES"
                          :value="true"
                          data-qe-id="resource_menu_edit_sso_yes"></v-radio>
                      <v-radio
                          :label="ypoResourcesMenuDialogFields.NO"
                          :value="false"
                          data-qe-id="resource_menu_edit_sso_no"></v-radio>
                    </v-flex>
                  </v-radio-group>
                </v-flex>
              </v-flex>
            </v-layout>
            <v-layout v-if="resourceMenuEditModel && resourceMenuEditModel.type === ypoResourcesCategoryType.BUTTON" row
                      wrap>
              <v-flex class="details-view">
                <span class="select-label">{{ ypoResourcesMenuDialogFields.DIALOG_LABEL }}</span>
                <v-text-field
                    v-model="resourceMenuEditModel.label"
                    :maxlength="ypoResourcesValidationRules.BUTTON_NAME_MAX_TEXT_LENGTH"
                    :placeholder="resourceMenuEditModel.label"
                    :rules="ypoResourcesValidationRules.CATEGORY_LABEL_RULE"
                    autofocus
                    color="ypo_blue"
                    required
                >
                </v-text-field>
                <span class="select-label">{{ ypoResourcesMenuDialogFields.DIALOG_ALT_TEXT }}</span>
                <v-text-field
                    v-model="resourceMenuEditModel.alternativeText"
                    :maxlength="ypoResourcesValidationRules.CATEGORY_NAME_MAX_TEXT_LENGTH"
                    :placeholder="resourceMenuEditModel.alternativeText"
                    color="ypo_blue"
                >
                </v-text-field>
                <span class="select-label">{{ ypoResourcesMenuDialogFields.DIALOG_LINK }}</span>
                <v-text-field
                    v-model="resourceMenuEditModel.navigationUrl"
                    :placeholder="resourceMenuEditModel.navigationUrl"
                    :rules="ypoResourcesValidationRules.NAVIGATION_URL_RULE"
                    color="ypo_blue"
                    required
                >
                </v-text-field>
                <v-flex xs2>
                  <span class="select-label">{{ ypoResourcesMenuDialogFields.DIALOG_SSO }}</span>
                  <v-radio-group v-model="resourceMenuEditModel.isSSOEnabled" text-xs-center>
                    <v-flex class="form-control">
                      <v-radio
                          :label="ypoResourcesMenuDialogFields.YES"
                          :value="true"
                          data-qe-id="resource_menu_edit_sso_yes"></v-radio>
                      <v-radio
                          :label="ypoResourcesMenuDialogFields.NO"
                          :value="false"
                          data-qe-id="resource_menu_edit_sso_no"></v-radio>
                    </v-flex>
                  </v-radio-group>
                </v-flex>
              </v-flex>
            </v-layout>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-layout pb-2 pt-1 row wrap class="relative-position">
            <v-flex xs12>
              <status-bar :action="updateResourceAction"></status-bar>
              <status-bar :action="updateCategoryAction"></status-bar>
              <status-bar :action="updateResourceButtonsAction"></status-bar>
            </v-flex>
            <v-flex pa-3 v-if="deleteTriggered">
              <v-layout row pt-3 pb-2>
                <v-flex xs-6 pt-1 class="bold-font">
                  {{ ypoResourcesConstant.DELETE_CONFIRMATION_MESSAGE }}
                </v-flex>
                <v-flex xs-6 class="text-right">
                  <v-btn class="primary_btn transparent" data-qe-id="cancel_dialog_btn" flat small
                         @click="deleteTriggered = false">
                    {{ appCommonConstants.CANCEL }}
                  </v-btn>
                  <v-btn :disabled="!isValid || isSaveInProgress" class="primary_btn" data-qe-id="save_dialog_btn"
                         @click="confirmDeleteAction(resourceMenuEditModel)">
                    {{ appCommonConstants.CONFIRM }}
                  </v-btn>
                </v-flex>
              </v-layout>
            </v-flex>
            <v-flex pa-3 text-xs-right v-else>
              <v-btn class="primary_btn transparent" data-qe-id="delete_dialog_btn" flat small
                     v-if="displayDeleteButton(resourceMenuEditModel)"
                     @click="deleteTriggered = true">
                {{ appCommonConstants.DELETE }}
              </v-btn>
              <v-btn class="primary_btn transparent" data-qe-id="cancel_dialog_btn" flat small
                     @click="closeResourceMenuDialog">
                {{ appCommonConstants.CANCEL }}
              </v-btn>
              <v-btn :disabled="!isValid || isSaveInProgress" class="primary_btn" data-qe-id="save_dialog_btn"
                     @click="saveResourceMenuAction">
                {{ appCommonConstants.SAVE }}
              </v-btn>
            </v-flex>
          </v-layout>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { storeAction } from "../../store/storeActions.constant";
import { mapActions, mapGetters, mapState, mapMutations } from "vuex";
import {
  ypoResourcesConstant,
  ypoResourcesCategoryType,
  ypoResourcesMenuDialogFields,
  ypoResourcesValidationRules, getDefaultResourceModel,
} from "./ypoResources.constant";
import { storeGetters } from "../../store/storeGetters.constant";
import { appCommonConstants, routerNames } from "../appCommon.constants.js";
import StatusOverlay from "../StatusOverlay/StatusOverlay";
import AdministratorTools from "../Tools/AdministratorTools";
import YpoToggleButton from "../YpoComponents/YpoToggleButton";
import { storeMutation } from "../../store/storeMutations.constant";
import StatusBar from "@/components/StatusBar/StatusBar";
import store from "@/store/store";
import { gaDataLayerEvent } from "@/plugins/analytics";
import { gtmEventName } from "@/plugins/gtmAnalytics";
import { getBrowserName } from "@/app.utils";
import { trackPageLoadTime } from '../../common/utils/performance';


export default {
  name: "ResourceMenuPage",
  components: {
    YpoToggleButton,
    AdministratorTools,
    StatusOverlay,
    StatusBar,
  },
  data: () => ({
    resourceAdImage: null,
    resourceButtonsList: null,
    resourceCategories: null,
    displayAlphabetically: false,
    ypoResourcesConstant,
    ypoResourcesCategoryType,
    ypoResourcesMenuDialogFields,
    ypoResourcesValidationRules,
    countPerCategory: 12,
    resourceMenuEditDialog: false,
    appCommonConstants,
    headerLabel: '',
    valid: false,
    isSaveInProgress: false,
    resourceType: '',
    deleteTriggered: false,
    resourceMenuEditModel: getDefaultResourceModel(),
    regionalDirector: 'Regional Director',
    networkDirector: 'Network Director',
  }),
  props: {
    showHideTools: {
      type: Function,
    },
  },
  mounted() {
            this.$nextTick(() => {
                trackPageLoadTime('YpoResources');
            });
  },
  async created() {
    const resourcesList = await this.getYpoResources();
    this.countPerCategory = resourcesList.countPerCategory || 12;
    this.getCanViewSpeakerAdminDirectory();
  },
  methods: {
    ...mapActions({
      getYpoResources: `ypoResources/${storeAction.getYpoResources}`,
      saveYpoResourcesCategories: `ypoResources/${storeAction.saveYpoResourcesCategories}`,
      saveYpoResourcesLinks: `ypoResources/${storeAction.saveYpoResourcesLinks}`,
      saveYpoResourcesButtons: `ypoResources/${storeAction.saveYpoResourcesButtons}`,
      getCanViewSpeakerAdminDirectory: `speaker/${storeAction.getCanViewSpeakerAdminDirectory}`,
      registerInAdobe: `auth/${storeAction.registerInAdobe}`,
    }),
    ...mapMutations({
      handleShowMoreForCategory: `ypoResources/${storeMutation.handleShowMoreForCategory}`,
    }),
    createNewCategory() {
      this.resourceMenuEditModel = getDefaultResourceModel();
      this.resourceMenuEditModel.type = ypoResourcesCategoryType.CATEGORY;
      this.resourceMenuEditDialog = true;
    },
    closeResourceMenuDialog() {
      this.isSaveInProgress = false;
      this.resourceMenuEditDialog = false;
      this.deleteTriggered = false;
      this.resourceMenuEditModel = getDefaultResourceModel();
      this.headerLabel = "";
    },
    handleNavigation(url, isSSOEnabled, title, buttonIndex) {
      if (title && (title.includes(ypoResourcesConstant.MY_LEARNING)
          || url.includes(ypoResourcesConstant.ABODE_CAPTIVATE_PRIME)
          || url.includes(ypoResourcesConstant.IS_LMS))) {
        this.registerInAdobe()
            .then((response) => {
              if (response && response.data) {
                if (this.isMobileAppBrowser || getBrowserName() === "safari") {
                  window.open(`${url}`, '_self');
                  } else {
                  window.open(`${url}`, '_blank');
                  }
              } else {
                store.commit(`ypoError/${storeMutation.setYpoErrorData}`, 1, { root: true });
                this.$router.push({
                  path: `/${routerNames.ERROR_PAGE}`,
                });
              }
            });
      } else {
        const appOrigin = window.location.origin;
        if (url && !url.includes('http')) {
          url = `https://${url}`;
        }
        if (url.includes(appOrigin) || (this.isMobileAppBrowser && isSSOEnabled) || getBrowserName() === "safari") {
          window.open(url, "_self");
        } else {
          window.open(url, "_blank");
        }
      }
      if (buttonIndex) {
        gaDataLayerEvent(gtmEventName.C_Resource_Page_Ad_Buttons, `Ad Button-${buttonIndex} | ${url}`);
      } else {
        gaDataLayerEvent(gtmEventName.C_Resource_Page_Resource_link, title);
      }
    },
    toggleDisplay() {
      this.displayAlphabetically = !this.displayAlphabetically;
    },
    showResourceLink(indexNumber) {
      const { countPerCategory, hideCategoriesWithExtensiveLinks } = this;
      return indexNumber < countPerCategory || (indexNumber >= countPerCategory && !hideCategoriesWithExtensiveLinks);
    },
    displayShowMoreButton(category) {
      return category.subCategories && category.subCategories.length > this.countPerCategory
          && this.hideCategoriesWithExtensiveLinks;
    },
    showMoreLinksForCategory() {
      this.handleShowMoreForCategory();
    },
    openEditResourceDialog(ypoResource, resourceType) {
      this.resourceMenuEditDialog = true;
      this.headerLabel = '';
      this.resourceType = '';
      this.resourceType = resourceType;
      this.headerLabel = ypoResource && (ypoResource.categoryName || ypoResource.resourceName || ypoResource.label)
          ? ypoResource.categoryName || ypoResource.resourceName || ypoResource.label : '';
      this.resourceMenuEditModel = {
        id: ypoResource.id,
        categoryName: ypoResource && ypoResource.categoryName ? ypoResource.categoryName : '',
        type: resourceType,
        isActive: ypoResource.isActive,
        navigationUrl: ypoResource && ypoResource.navigationUrl ? ypoResource.navigationUrl : '',
        isSSOEnabled: ypoResource && ypoResource.isSSOEnabled ? ypoResource.isSSOEnabled : false,
        alternativeText: ypoResource && ypoResource.alternativeText ? ypoResource.alternativeText : '',
        resourceCategoryId: ypoResource && ypoResource.resourceCategoryId ? ypoResource.resourceCategoryId : null,
        resourceName: ypoResource && ypoResource.resourceName ? ypoResource.resourceName || ypoResource.resourceName : '',
        displayOrder: ypoResource && ypoResource.displayOrder ? ypoResource.displayOrder : '',
        imageUrl: ypoResource && ypoResource.imageUrl ? ypoResource.imageUrl : '',
        label: ypoResource && ypoResource.label ? ypoResource.label : '',
      };
    },
    openAddResourceDialog(resourceType, categoryId) {
      this.resourceMenuEditDialog = true;
      this.headerLabel = '';
      this.resourceType = '';
      this.resourceType = resourceType;
      this.headerLabel = resourceType && resourceType === this.ypoResourcesCategoryType.LINK
          ? this.ypoResourcesMenuDialogFields.DIALOG_NEW_LINK_LABEL : this.ypoResourcesMenuDialogFields.DIALOG_NEW_CATEGORY_LABEL;
      this.resourceMenuEditModel = {};
      this.resourceMenuEditModel = {
        categoryName: null,
        type: resourceType,
        isActive: true,
        navigationUrl: null,
        isSSOEnabled: false,
        alternativeText: null,
        resourceCategoryId: categoryId,
        resourceName: null,
        label: null,
      };
    },
    async saveResourceMenuAction() {
      this.isSaveInProgress = true;
      if (this.resourceMenuEditModel.type === this.ypoResourcesCategoryType.CATEGORY) {
        await this.saveYpoResourcesCategories(this.resourceMenuEditModel);
      } else if (this.resourceMenuEditModel.type === this.ypoResourcesCategoryType.LINK) {
        await this.saveYpoResourcesLinks(this.resourceMenuEditModel);
      } else if (this.resourceMenuEditModel.type === this.ypoResourcesCategoryType.BUTTON) {
        await this.saveYpoResourcesButtons(this.resourceMenuEditModel);
      }
      this.closeResourceMenuDialog();
    },
    displayDeleteButton(resourceMenuEditModel) {
      return resourceMenuEditModel.id && resourceMenuEditModel.type === this.ypoResourcesCategoryType.LINK;
    },
    async confirmDeleteAction(resource) {
      await this.saveYpoResourcesLinks({
        ...resource,
        isActive: false,
      });
      this.closeResourceMenuDialog();
    },
  },
  computed: {
    ...mapGetters({
      profileInformation: `myAccount/${storeGetters.profileInformation}`,
      ypoResources: `ypoResources/${storeGetters.ypoResources}`,
      hideCategoriesWithExtensiveLinks: `ypoResources/${storeGetters.hideCategoriesWithExtensiveLinks}`,
      isMobileAppBrowser: `${[storeGetters.isMobileAppBrowser]}`,
      canViewSpeakerAdminDirectory: `speaker/${storeGetters.canViewSpeakerAdminDirectory}`,
      prpRole: `chapterWaiver/${storeGetters.prpRole}`,
      isPRPAdmin: `chapterWaiver/${storeGetters.isPRPAdmin}`,
      isPRPMember: `chapterWaiver/${storeGetters.isPRPMember}`,
      canViewManageChapterStudio: `auth/${storeGetters.canViewManageChapterStudioPage}`,
      usersCommunitiesList: `chapter/${storeGetters.usersCommunitiesList}`,
      isChaptersAndRegionsTeamMember: `auth/${storeGetters.isChaptersAndRegionsTeamMember}`,
    }),
    ...mapState('actionState', ['actions']),
    loadingResources() {
      return this.actions[storeAction.getYpoResources];
    },
    isRegionalDirector() {
      return (
        Array.isArray(this.usersCommunitiesList) &&
        this.usersCommunitiesList.some(
          x => Array.isArray(x.roles) && x.roles.includes(this.regionalDirector)
        )
      );
    },
    isNetworkDirector() {
      return (
        Array.isArray(this.usersCommunitiesList) &&
        this.usersCommunitiesList.some(
          x => Array.isArray(x.roles) && x.roles.includes(this.networkDirector)
        )
      );
    },
    adminLinksExists() {
      return !!((this.profileInformation && (this.profileInformation.salesforceAccessfromExchange ||
              this.profileInformation.isChapterForumOfficer ||
              this.profileInformation.cventAccessfromExchange))
          || (this.prpRole && (this.isPRPAdmin || this.isPRPMember)));
    },
    hasAdminToolsAccess() {
      return this.adminLinksExists || this.canViewSpeakerAdminDirectory || this.canViewManageChapterStudio || this.isRegionalDirector || this.isNetworkDirector || this.isChaptersAndRegionsTeamMember;
    },
    categoriesList() {
      let result = [];
      if (this.ypoResources) {
        result = this.displayAlphabetically ? this.ypoResources.resourcesByAlphabeticalOrder : this.ypoResources.resourcesByCategory;
      }
      return result;
    },
    updateResourceAction() {
      return this.actions[storeAction.saveYpoResourcesLinks];
    },
    updateCategoryAction() {
      return this.actions[storeAction.saveYpoResourcesCategories];
    },
    updateResourceButtonsAction() {
      return this.actions[storeAction.saveYpoResourcesButtons];
    },
    isValid() {
      let val;
      if (this.resourceMenuEditModel.type === this.ypoResourcesCategoryType.BUTTON) {
        val = this.resourceMenuEditModel.navigationUrl && this.resourceMenuEditModel.label;
      } else if (this.resourceMenuEditModel.type === this.ypoResourcesCategoryType.LINK) {
        val = this.resourceMenuEditModel.navigationUrl && this.resourceMenuEditModel.resourceName;
      } else if (this.resourceMenuEditModel.type === this.ypoResourcesCategoryType.CATEGORY) {
        val = this.resourceMenuEditModel.categoryName;
      }
      return val && this.valid;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../common/styles/_colors.scss";
@import "../../common/styles/_responsive.scss";

.ypo-resources {
  padding: 10px 25px;
  max-width: 350px;
  margin: 64px auto 0;

  @include breakpointMin(sm) {
    padding: 10px 40px;
    max-width: 1700px;
  }

  &__header {
    padding-bottom: 10px;
    display: flex;

    &--title {
      font-size: 30px;
      color: $ypo-blue;

      @include breakpointMin(sm) {
        padding-left: 20px;
      }

      @include breakpointMin(md) {
        padding-left: 5px;
      }
    }

    &--toggle {
      margin-top: 12px;
      margin-left: 20px;

      @include breakpoint(xs) {
        margin-left: 35px;
      }
    }
  }

  &__content {
    position: relative;
  }

  &__main {
    display: flex;
    flex-wrap: wrap;
  }

  &__create-category {
    width: 100%;
    border: 1px solid $ypo-gray;
    background: $white;
    border-radius: 4px;
    font-weight: 600;
    margin: 0;

    @include breakpoint(sm) {
      display: none;
    }
  }

  &__ad-panel {
    width: 100%;
    max-width: 675px;
    max-height: 350px;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-evenly;

    @include breakpointMin(md) {
      max-width: 320px;
      padding-right: 10px;
    }

    &--image {
      cursor: pointer;
      width: 100%;
      max-width: 320px;
      min-width: 300px;
      height: 180px;
      margin-bottom: 15px;

      &:hover {
        border-color: $ypo-gray;
        box-shadow: 0 1px 5px 0 rgba($black, 0.36);
      }

    }

    &--button-list {
      max-width: 300px;
      display: flex;
      flex-direction: column;
      flex-wrap: wrap;
      justify-content: space-evenly;
    }
  }

  &__category-panel {
    width: 100%;
    max-width: 675px;
    margin-top: 10px;

    @include breakpointMin(sm) {
      padding: 0 30px;
    }

    @include breakpointMin(md) {
      width: calc(100% - 320px);
      max-width: 100%;
      margin-top: 0;
      padding-left: 5px;
      padding-right: 0;
    }

    &--main {
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;

      @include breakpointMin(md) {
        justify-content: start;
      }
    }

    .ypo-resources__category {
      display: inline-grid;
      width: 100%;
      max-width: 315px;
      padding: 3px;

      @include breakpointMin(sm) {
        max-width: 305px;
        padding: 5px 15px 10px 0;
      }

      @include breakpointMin(lg) {
        max-width: 275px;
      }

      &:nth-child(even) {
        @include breakpointMin(sm) {
          padding: 5px 0 10px 15px;
        }
        @include breakpointMin(md) {
          padding: 5px 15px 10px 0;
        }
      }

      .conduct-terms {
        margin: 0 !important;
      }

      &--header {
        padding: 5px 15px;
        font-family: 'Raleway', sans-serif;
        color: $ypo-blue;
        font-size: 18px;
        line-height: 34px;
        font-weight: 500;
        border-bottom: 1px solid rgba($ypo-gray, 0.4);
        overflow-wrap: break-word;
        word-break: break-all;

        i {
          font-size: 12px !important;
          color: $ypo-gray !important;
          cursor: pointer;
          position: absolute;
          display: none;
          top: 15px;
          right: 15px;

          &:hover {
            color: $ypo-cta !important;
          }
        }

        &:hover {
          i {
            display: block;
          }
          @include breakpoint(sm) {
            i { display: none }
          }
        }
      }

      &--content {
        padding: 0 15px 15px;
      }

      &--add-btn {
        margin-top: 6px;
        background: none;
        color: $ypo-blue;
        font-size: 13px;
        font-weight: 600;
        text-transform: capitalize;
        cursor: pointer;

        @include breakpoint(sm) {
          display: none;
        }
      }

      .resourceLink {
        padding: 7px 0 0;
        line-height: 16px;

        i {
          font-size: 12px !important;
          color: $ypo-gray !important;
          cursor: pointer;
          position: absolute;
          display: none;
          margin-top: -16px;
          right: 15px;

          &:hover {
            color: $ypo-cta !important;;
          }
        }

        &:hover {
          i {
            display: block;
            @include breakpoint(sm) {
              display: none;
            }
          }
        }

        &__label {
          color: $ypo-cta;
          font-size: 12px;
          cursor: pointer;

          &:hover {
            text-decoration: underline;
          }

        }

        &__show-more {
          position: absolute;
          right: 0;
          bottom: 0;
          width: 32px;
          height: 35px;
          background-color: $ypo-cta;

          i {
            color: $white;
            font-size: 10px;
            margin-left: 15px;
            margin-bottom: -2px;
          }

          &--icon {
            cursor: pointer;
            padding-top: 7px;
            margin-left: 3px;
            height: 100%;

            &:after {
              content: "";
              width: 0;
              height: 0;
              position: absolute;
              left: -6px;
              top: -17px;
              -webkit-transform: rotate(45deg);
              transform: rotate(43deg);
              border-top: 26px solid transparent;
              border-bottom: 25px solid transparent;
              border-right: 25px solid white;
              pointer-events: none;
              z-index: 2;
              cursor: none;
            }
          }
        }
      }
    }
  }

  .desktop-resources {
    display: none;

    @include breakpointMin(sm) {
      display: inline-grid;
    }
  }

  .mobile-resources {
    display: inline-grid;

    @include breakpointMin(sm) {
      display: none;
    }
  }

  &__ad-panel--button {
    max-width: 320px;
    min-width: 300px;
    margin-bottom: 15px;

    i {
      font-size: 12px !important;
      color: $ypo-medium-gray !important;
      cursor: pointer;
      position: absolute;
      display: none;
      margin-top: -32px;
      margin-left: 285px;

      &:hover {
        color: $white !important;
      }
    }

    &:hover {
      i {
        display: block;
        @include breakpoint(sm) {
          display: none;
        }
      }
    }
  }

  .ad-panel-buttons {
    color: white !important;
    border-radius: 4px;
    padding: 8px 0;
    font-size: 14px;
    font-weight: 600;
    -webkit-box-shadow: none;
    box-shadow: none;
    width: 100%;
    max-width: 320px;
    cursor: pointer;
    text-align: center;
    overflow-wrap: break-word;
    word-break: break-all;
  }

  .button0 {
    background: $ypo-blue !important;
  }

  .button1 {
    background: $ypo-blue-secondary !important;
  }

  .button2 {
    background: $ypo-gold !important;
  }
}

.categoryEdit-popup {
  .v-card__title {
    background-color: $ypo-tile-border;
    color: $ypo-blue;
    font-family: Raleway, "sans-serif";
    font-size: 24px;
    font-weight: 500;
    line-height: 30px;
    position: relative;

    i {
      color: $ypo-cta;
      position: absolute;
      right: 20px;
      cursor: pointer;
    }
  }

  .form-control {
    margin: 0 10px;
    min-height: 70px;
    position: relative;
  }

  .currently-work {
    position: absolute;
    right: 0;
  }

  .required-label {
    color: $ypo-blue;
    font-size: 14px;
    font-weight: bold;
    line-height: 24px;
    text-align: right;
    display: block;
  }

  .select-label {
    color: $ypo-blue;
    font-size: 14px;
    font-weight: bold;
    line-height: 20px;

    i {
      font-size: 18px;
      padding-left: 5px;
    }
  }

  .select-label-dateFeild {
    color: $ypo-blue;
    font-size: 14px;
    font-weight: bold;
    line-height: 20px;
    margin-left: 10px;

    i {
      font-size: 18px;
      padding-left: 5px;
    }
  }

  .error-label-enable {
    color: $ypo-red;
  }

  .error-label-disable {
    color: $ypo-blue;
  }

  h4 {
    color: $ypo-blue;
    font-family: Raleway, "sans-serif";
    font-size: 20px;
    font-weight: 500;
    line-height: 24px;
    margin: 0 0 5px 10px;
  }

  .check-list {
    margin: 0 5px 10px 5px;
    border-bottom: 2px solid rgba($black, 0.2);
    padding: 10px 0 10px 0;
  }

  .row__list {
    margin-bottom: 20px;
  }

  .btn-row {
    padding: 0 0 30px 15px;
    @media (max-width: 767px) {
      .v-btn {
        padding: 7px 20px;
      }
    }
    @include breakpoint(xs) {
      padding: 10px 0 10px 0;
    }
  }

  .v-card__text {
    margin-right: 5px;
    width: auto;
  }

  .v-card__actions {
    border-top: 1px solid $ypo-medium-gray;
  }

  .details-view {
    color: $ypo-gray-text;
    font-size: 14px;
    line-height: 24px;
    margin-bottom: 30px;
    padding-top: 30px;
    border-top: 1px solid $ypo-medium-gray;

    &:first-child {
      border-top: none;
      padding-top: 0;
    }

    h6 {
      color: $ypo-blue;
      font-size: 16px;
      font-weight: bold;
      line-height: 22px;
      margin: 0 0 5px 0;

      i {
        color: $ypo-title-blue-gray;
        margin-left: 5px;
        cursor: pointer;
      }

      .note {
        font-size: 14px;
        color: $ypo-gray;
        font-weight: 500;
        margin: 0 0 0 15px;
      }
    }

    strong {
      margin-left: 20px;
      display: block;

      i {
        color: $ypo-title-blue-gray;
        cursor: pointer;
        margin-left: 5px;
      }
    }

    small {
      margin-left: 20px;
      font-size: 14px;
      display: block;
    }

    i {
      color: $ypo-title-blue-gray;
      font-size: 14px;
      margin-left: 5px;
      cursor: pointer;
    }
  }

  .contact-edit {
    &__title {
      color: $ypo_blue;
      text-align: center;
      font-weight: 600;
      line-height: 16px;
      font-size: 12px;
      margin-bottom: 15px;
    }

    &__row {
      align-items: center;
      background: $ypo-light-gray-background;

      &:nth-child(2n + 1) {
        background: $white;
      }

      strong {
        display: block;
        text-align: right;
        margin-top: 20px;
        color: $ypo_blue;
      }
    }
  }

  .autocomplete {
    position: relative;

    .autocomplete-results {
      padding: 0;
      margin: 0;
      border: 1px solid $white;
      height: 200px;
      overflow: auto;
      background: $ypo-extra-light-gray;
      position: absolute;
      z-index: 100;
      width: 100%;
    }

    .autocomplete-result {
      list-style: none;
      text-align: left;
      padding: 4px 2px;
      cursor: pointer;

      &.is-active,
      &:hover {
        background-color: $ypo-cta;
        color: $black;
      }
    }
  }

  .v-btn__content {
    text-transform: uppercase;
  }

  .save-to-profile {
    display: initial;
    color: $ypo_blue;
    margin-left: 15px;
    font-weight: bold;

    i {
      margin-right: 8px;
    }

    @include breakpoint(xs) {
      display: block;
      margin: 10px 0;
    }
  }

  .add-edit-btn {
    position: relative;

    .fa-plus-circle {
      position: absolute;
      left: 6px;
      bottom: 0;
      font-size: 9px !important;
      background: $white;
      border-radius: 50%;
      padding: 1px;
    }
  }
}

</style>
<style lang="scss">
.ypo-resources {
  .administrator-tools {
    padding: 0px 15px 15px;
    
  }

  .v-radio.radio-item {
    margin-bottom: 37px;
    top: 3px;
    position: relative;
    left: 10px;
  }
}
</style>
